import { CssBaseline } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useMemo, type ReactNode } from 'react';

// FSE brand fonts — self-hosted (no Google Fonts CDN; POPIA-clean + offline-safe).
// Display: Bricolage Grotesque (variable) — registers family "Bricolage Grotesque Variable".
// Body: Noto Sans (broad multi-script coverage incl. latin-ext for SA languages).
// Icons: now self-hosted SVGs via the kit <Icon> (icons.generated.ts) — the 5.3 MB
// Material Symbols webfont was dropped here (TD-093).
import '@fontsource-variable/bricolage-grotesque';
import '@fontsource/noto-sans/400.css';
import '@fontsource/noto-sans/500.css';
import '@fontsource/noto-sans/600.css';
import '@fontsource/noto-sans/700.css';
// Roboto retained as a deep fallback (MUI's default metric reference).
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import { buildTheme } from './theme.js';
import { ColorModeProvider, useColorMode } from './ColorMode.js';

/**
 * Inner consumer — must live INSIDE ColorModeProvider so it can read the
 * current mode and rebuild the MUI theme on toggle.
 */
function InnerThemeProvider({ children }: { children: ReactNode }) {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildTheme(mode), [mode]);
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}

/**
 * Wraps the app with the FSE theme + color-mode tracking.
 *
 * Self-hosts Roboto via @fontsource (no Google Fonts CDN request — POPIA-
 * cleaner: zero third-party calls for fonts). Reads the saved color
 * preference from localStorage (`fse_color_mode`) and falls back to the
 * OS-level `prefers-color-scheme` when the preference is `'system'`.
 *
 * Consumers can toggle modes via the `useColorMode()` hook from `@fse/ui`.
 */
export function FseThemeProvider({ children }: { children: ReactNode }) {
  return (
    <ColorModeProvider>
      <InnerThemeProvider>{children}</InnerThemeProvider>
    </ColorModeProvider>
  );
}
